<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useCountUp } from '@/composables/useCountUp'
import { useReducedMotion } from '@/composables/useReducedMotion'

const props = withDefaults(
  defineProps<{
    count: number
    size?: 'sm' | 'md' | 'lg'
    label?: string
  }>(),
  {
    size: 'md',
  },
)

const safeCount = computed(() => (Number.isFinite(props.count) ? props.count : 0))

const displayCount = useCountUp(() => safeCount.value)

const SIZE_MAP = {
  sm: 'small',
  md: 'default',
  lg: 'large',
} as const

const vuetifySize = computed(() => SIZE_MAP[props.size ?? 'md'])

const ariaLabel = computed(() => {
  if (props.label) return props.label
  const n = safeCount.value
  return n === 1 ? '1 star' : `${n} stars`
})

const reducedMotion = useReducedMotion()
const isPopping = ref(false)
let isMounted = false

onMounted(() => {
  isMounted = true
})

watch(safeCount, (newVal, oldVal) => {
  if (isMounted && newVal !== oldVal && !reducedMotion.value) {
    isPopping.value = true
  }
})

function onAnimationEnd() {
  isPopping.value = false
}
</script>

<template>
  <v-chip
    variant="flat"
    :size="vuetifySize"
    :class="[
      'fb-star-chip',
      `fb-star-chip--${size}`,
      'tabular-nums',
      { 'fb-pop': isPopping },
    ]"
    :aria-label="ariaLabel"
    @animationend="onAnimationEnd"
  >
    <template #prepend>
      <v-icon icon="mdi-star" aria-hidden="true" />
    </template>
    {{ Math.round(displayCount) }}
  </v-chip>
</template>
